import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { DailyActivityRow, ReportFilters } from '../../../core/models/report.model';
import { PermissionService } from '../../../core/services/permission.service';
import { ReportsService } from '../../../core/services/reports.service';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { CardActionsComponent } from '../../../shared/components/card/card-actions.component';
import { CardContentComponent } from '../../../shared/components/card/card-content.component';
import { CardHeaderComponent } from '../../../shared/components/card/card-header.component';
import { CardTitleComponent } from '../../../shared/components/card/card-title.component';
import { CardComponent } from '../../../shared/components/card/card.component';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { ReportFilterBarComponent } from '../../../shared/components/report-filter-bar/report-filter-bar.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { TranslatePipe } from '../../../shared/pipes/translate.pipe';
import { exportToCSV, exportToXLSX, generateFilename } from '../../../shared/utils/export.utils';

/** "Per canvasser: doors, contacts, contact rate, leads, conversion" (design doc §8). */
@Component({
  selector: 'app-daily-activity',
  standalone: true,
  imports: [
    ButtonComponent,
    CardComponent,
    CardHeaderComponent,
    CardTitleComponent,
    CardContentComponent,
    CardActionsComponent,
    IconComponent,
    ReportFilterBarComponent,
    SpinnerComponent,
    TranslatePipe,
  ],
  templateUrl: './daily-activity.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./daily-activity.component.css', '../../../../css/report-print.css'],
})
export class DailyActivityComponent {
  private reportsService = inject(ReportsService);
  private permissionService = inject(PermissionService);

  includeAllUsers = this.permissionService.has('interaction.read-others');
  canExport = this.permissionService.has('report.export');

  rows = signal<DailyActivityRow[]>([]);
  loading = signal(false);

  async onFiltersChange(filters: ReportFilters): Promise<void> {
    this.loading.set(true);
    try {
      this.rows.set(await this.reportsService.getDailyActivity(filters, this.includeAllUsers));
    } finally {
      this.loading.set(false);
    }
  }

  exportCsv(): void {
    exportToCSV(this.exportRows(), generateFilename('daily-activity'));
  }

  exportXlsx(): void {
    exportToXLSX(this.exportRows(), generateFilename('daily-activity'), 'Daily Activity');
  }

  private exportRows(): Record<string, string | number>[] {
    return this.rows().map((r) => ({
      User: r.userName,
      Doors: r.doors,
      Contacts: r.contacts,
      'Contact rate': `${(r.contactRate * 100).toFixed(1)}%`,
      Leads: r.leads,
      'Conversion rate': `${(r.conversionRate * 100).toFixed(1)}%`,
    }));
  }
}
